import { ACTIVITY_ICON_MAP } from "@storytell/chat-ui/message/OperationHeader";
import { ChatMessages } from "@storytell/cp-client";
import type {
  ActivityCategory,
  StreamingBlock,
  StreamingChatId,
  StreamingMessage,
  StreamingMessageId,
} from "@storytell/streaming";
import {
  getActivityCategory,
  getBlockContent,
  getBranchPosition,
  getCategoryIconKey,
  isTimelineTool,
  StreamingMessageStatus,
} from "@storytell/streaming";
import { StTooltip, StTooltipSuppressionProvider, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbFileText, TbGitBranch } from "solid-icons/tb";
import { createEffect, createMemo, createSignal, For, mapArray, onCleanup, onMount, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useChatScrollContextSafe } from "~/components/chat/contexts/ChatScrollContext";
import { useI18n } from "~/i18n";
import { trackFeature } from "~/lib/trackFeature";
import { formatRelativeTimeLong } from "~/utils/chatListAudit";
import { useWire } from "~/wire";

interface TimelinePrompt {
  messageId: StreamingMessageId;
  text: string;
  previewText: string;
  timestamp: Date;
  toolsUsed: ActivityCategory[];
  username: string;
  isPending: boolean;
  hasBranches: boolean;
}

const samePrompt = (a: TimelinePrompt | null, b: TimelinePrompt | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.messageId === b.messageId &&
    a.text === b.text &&
    a.timestamp.getTime() === b.timestamp.getTime() &&
    a.username === b.username &&
    a.isPending === b.isPending &&
    a.hasBranches === b.hasBranches &&
    a.toolsUsed.length === b.toolsUsed.length &&
    a.toolsUsed.every((tool, i) => tool === b.toolsUsed[i]));

// Message renderers by id, from one walk of the chat instead of an attribute-selector
// query per prompt.
const indexMessageElements = (root: ParentNode): Map<string, HTMLElement> => {
  const elements = new Map<string, HTMLElement>();
  for (const el of root.querySelectorAll<HTMLElement>("[data-message-renderer]")) {
    elements.set(el.getAttribute("data-message-renderer") ?? "", el);
  }
  return elements;
};

interface PromptTimelineProps {
  scrollContainerRef: () => HTMLDivElement | undefined;
  chatId: () => string | undefined;
  /** Always show the timeline; without it the timeline never renders */
  forceVisible?: boolean;
  /** Center timeline in viewport instead of parent container */
  centerInViewport?: boolean;
}

const extractTextFromRichText = (doc: { type?: string; content?: unknown[]; text?: string }): string => {
  if (doc.type === "text") {
    return doc.text || "";
  }
  if (doc.content && Array.isArray(doc.content)) {
    return doc.content
      .map((node) => extractTextFromRichText(node as { type?: string; content?: unknown[]; text?: string }))
      .join("");
  }
  return "";
};

const findMessageElement = (messageId: StreamingMessageId): HTMLElement | null => {
  return document.querySelector(`[data-message-renderer="${messageId}"]`) as HTMLElement | null;
};

// A prompt re-derives on every operation and block change in its message, so
// each completed block is parsed once. A streaming block is skipped: its JSON
// can't parse until it's whole, and completion re-runs the memo anyway.
const getBlockType = (block: StreamingBlock, blockTypes: Map<string, string | null>): string | null => {
  if (!block.isComplete) return null;
  const cached = blockTypes.get(block.blockId);
  if (cached !== undefined) return cached;
  const text =
    block.finalContent?.type === "text" && block.finalContent.text
      ? block.finalContent.text
      : getBlockContent(block.blockId);
  if (!text) return null;
  let type: string | null = null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === "object" && parsed !== null && "type" in parsed && typeof parsed.type === "string") {
      type = parsed.type;
    }
  } catch {
    // Plain text, not a typed payload.
  }
  blockTypes.set(block.blockId, type);
  return type;
};

/** Vertical timeline showing prompts as clickable dots. */
export const PromptTimeline = (props: PromptTimelineProps) => {
  const wire = useWire();
  const { t } = useI18n();
  const chatScroll = useChatScrollContextSafe();
  const stream = wire.stream;

  const chat = createMemo(() => {
    const chatId = props.chatId();
    if (!chatId) return undefined;
    return stream.chat.get(chatId as StreamingChatId);
  });

  /** Get activity categories for tools used in the message's answer */
  const getToolsUsed = (
    message: StreamingMessage,
    promptOperationId: string | undefined,
    blockTypes: Map<string, string | null>
  ): ActivityCategory[] => {
    const toolsUsed = new Set<ActivityCategory>();

    if (!message || !message.operations || message.operations.length === 0) return [];

    for (const operationId of message.operations) {
      if (operationId === promptOperationId) continue;

      const operation = stream.operation.get(operationId);
      if (!operation) continue;

      const activity = operation.activity;

      if (isTimelineTool(activity)) {
        const category = getActivityCategory(activity);
        if (category !== "unknown") {
          toolsUsed.add(category);
        }
      }

      if (operation.blocks && operation.blocks.length > 0) {
        for (const blockId of operation.blocks) {
          const block = stream.block.get(blockId);
          if (!block) continue;

          if (block.webSearchResults) {
            toolsUsed.add("web_search");
          }

          const type = getBlockType(block, blockTypes);
          if (type === "image") toolsUsed.add("image");
          if (type === "presentation") toolsUsed.add("presentation");
        }
      }
    }

    return Array.from(toolsUsed);
  };

  const [timeUpdateTrigger, setTimeUpdateTrigger] = createSignal(0);

  // Update relative times every 60 seconds (precision beyond minutes is unnecessary)
  onMount(() => {
    const intervalId = setInterval(() => {
      setTimeUpdateTrigger((prev) => prev + 1);
    }, 60000);

    onCleanup(() => {
      clearInterval(intervalId);
    });
  });

  const messages = createMemo(() => {
    const active = chatScroll?.activeMessageIds();
    if (active && active.length > 0) return active;
    return chat()?.messages || [];
  });

  // Sibling lookup is O(messages²); kept out of the per-prompt memos, which re-run on
  // every operation and block change.
  const branchingMessageIds = createMemo(() => {
    const currentChat = chat();
    const allChatMsgIds = currentChat?.messages;
    if (currentChat?.hasBranches === false || !allChatMsgIds) return new Set<StreamingMessageId>();
    return new Set(allChatMsgIds.filter((id) => getBranchPosition(stream.state.messages, id, allChatMsgIds) !== null));
  });

  // One memo per message, so an operation or block change re-derives only its own
  // message, and a prompt keeps its object (and its dot stays mounted) until
  // something the dot shows changes.
  const promptEntries = createMemo(
    mapArray(messages, (messageId) => {
      const blockTypes = new Map<string, string | null>();
      // Stable fallback, so a prompt with no started operation still compares equal across runs.
      const firstSeen = new Date();

      return createMemo<TimelinePrompt | null>(
        () => {
          const message = stream.message.get(messageId);

          if (!message) {
            return null;
          }
          if (message.role !== ChatMessages.Role.USER) {
            return null;
          }

          const prompt = message.prompt;
          if (!prompt) {
            return null;
          }

          let promptText = prompt.text || "";
          if (!promptText && prompt.richText) {
            promptText = extractTextFromRichText(prompt.richText);
          }

          if (!promptText.trim() && !prompt.richText) {
            return null;
          }

          if (!promptText.trim() && prompt.richText) {
            promptText = "[Prompt]";
          }

          let timestamp: Date | undefined;

          const promptOperation = stream.operation.get(prompt.operationId);
          if (promptOperation?.startedAt) {
            timestamp = promptOperation.startedAt;
          } else {
            const messageOperations = message.operations || [];
            for (const opId of messageOperations) {
              const op = stream.operation.get(opId);
              if (op?.startedAt) {
                if (!timestamp || op.startedAt < timestamp) {
                  timestamp = op.startedAt;
                }
              }
            }
          }

          const toolsUsed = getToolsUsed(message, prompt.operationId, blockTypes);

          const isPending = message.status === StreamingMessageStatus.GENERATING && toolsUsed.length === 0;

          const currentUser = wire.auth.user();
          const createdByUserId = message.createdBy;
          let username = message.createdByEmail || t("label_someone_else");
          if (createdByUserId && currentUser?.id === createdByUserId) {
            username = currentUser.displayName || t("common.you");
          }

          return {
            messageId: messageId,
            text: promptText,
            previewText: promptText.length > 150 ? `${promptText.substring(0, 150)}...` : promptText,
            timestamp: timestamp || firstSeen,
            toolsUsed,
            username,
            isPending,
            hasBranches: branchingMessageIds().has(messageId),
          };
        },
        null,
        { equals: samePrompt }
      );
    })
  );

  const prompts = createMemo(() => promptEntries().flatMap((entry) => entry() ?? []), [], {
    equals: (a, b) => a.length === b.length && a.every((p, i) => p === b[i]),
  });

  // Track scroll position as a percentage (0-100) for the slider indicator
  const [scrollPercentage, setScrollPercentage] = createSignal(0);

  const scrollToPrompt = (messageId: StreamingMessageId) => {
    const container = props.scrollContainerRef();
    if (!container) return;

    const messageElement = findMessageElement(messageId);
    if (!messageElement) return;

    const promptElement =
      (messageElement.querySelector('[data-renderer="prompt-ack-renderer"]') as HTMLElement) || messageElement;

    const containerRect = container.getBoundingClientRect();
    const elementRect = promptElement.getBoundingClientRect();

    const scrollOffset = elementRect.top - containerRect.top + container.scrollTop - 100;

    container.scrollTo({
      top: scrollOffset,
      behavior: "smooth",
    });
  };

  createEffect(() => {
    const container = props.scrollContainerRef();
    if (!container) return;

    let rafId: number | null = null;
    let isUpdating = false;

    const handleScroll = () => {
      if (!isUpdating) {
        isUpdating = true;
        rafId = requestAnimationFrame(() => {
          const maxScroll = container.scrollHeight - container.clientHeight;
          const percentage = maxScroll > 0 ? (container.scrollTop / maxScroll) * 100 : 0;
          setScrollPercentage(Math.max(0, Math.min(100, percentage)));
          isUpdating = false;
          rafId = null;
        });
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    // Scroll events miss layout moving under a still scrollTop: a shared chat
    // rendering its recording after the fetch, a turn growing mid-stream, a
    // resize. The observer covers those and fires once on observe for the
    // initial update; it shares the scroll path's frame, so both together
    // update at most once per frame.
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(container);
    for (const child of container.children) resizeObserver.observe(child);

    onCleanup(() => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    });
  });

  const shouldShow = createMemo(() => {
    if (!props.forceVisible) return false;
    const chatData = chat();
    if (!chatData) return false;
    const promptsList = prompts();
    return promptsList.length >= 1;
  });

  const isStreaming = (): boolean => wire.chat.isChatStreaming();

  return (
    <Show when={shouldShow()}>
      <PromptTimelineInner
        prompts={prompts}
        scrollPercentage={scrollPercentage}
        scrollToPrompt={scrollToPrompt}
        scrollContainerRef={props.scrollContainerRef}
        getRelativeTime={formatRelativeTimeLong}
        timeUpdateTrigger={timeUpdateTrigger}
        isStreaming={isStreaming}
        t={t}
        forceVisible={props.forceVisible}
        centerInViewport={props.centerInViewport}
        setTimelinePointerActive={chatScroll?.setTimelinePointerActive}
      />
    </Show>
  );
};

const PromptTimelineInner = (props: {
  prompts: () => Array<{
    messageId: StreamingMessageId;
    text: string;
    previewText: string;
    timestamp: Date;
    toolsUsed: ActivityCategory[];
    username: string;
    isPending: boolean;
    hasBranches: boolean;
  }>;
  scrollPercentage: () => number;
  scrollToPrompt: (messageId: StreamingMessageId) => void;
  scrollContainerRef: () => HTMLDivElement | undefined;
  getRelativeTime: (timestamp: Date) => string;
  timeUpdateTrigger: () => number;
  isStreaming: () => boolean;
  t: ReturnType<typeof useI18n>["t"];
  forceVisible?: boolean;
  centerInViewport?: boolean;
  /** When provided (e.g. from ChatScrollContext), called when user starts/stops interacting with the timeline bar or blue dot so "Show panel" tooltip can be shown during interaction. */
  setTimelinePointerActive?: (active: boolean) => void;
}) => {
  // Hide the latest prompt dot while streaming
  const displayedPrompts = createMemo(() => {
    const p = props.prompts();
    if (props.isStreaming() && p.length > 1) return p.slice(0, -1);
    return p;
  });

  const [isDraggingDot, setIsDraggingDot] = createSignal(false);

  const scrollToPercent = (percent: number) => {
    const container = props.scrollContainerRef();
    if (!container) return;
    const p = Math.max(0, Math.min(100, percent));
    const maxScroll = container.scrollHeight - container.clientHeight;
    container.scrollTop = (p / 100) * maxScroll;
  };

  /** Scroll to position or nearest prompt on timeline click */
  const handleTimelineClick = (e: MouseEvent) => {
    const prompts = displayedPrompts();
    const container = props.scrollContainerRef();
    if (!container || prompts.length === 0) return;
    trackFeature({ feature: "chat.timeline_jump", via: "button" });

    const timelineContainer = e.currentTarget as HTMLElement;
    const timelineRect = timelineContainer.getBoundingClientRect();
    const clickY = e.clientY - timelineRect.top;
    const tlHeight = timelineRect.height;

    const dotPositions = dotPositionsPercent().map((percent) => (percent / 100) * tlHeight);
    const SNAP_THRESHOLD = 20; // pixels - scroll to prompt if click is within 20px of a dot

    let nearestDotIndex: number | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    dotPositions.forEach((dotCenterY, index) => {
      const distance = Math.abs(clickY - dotCenterY);
      if (distance < nearestDistance && distance <= SNAP_THRESHOLD) {
        nearestDistance = distance;
        nearestDotIndex = index;
      }
    });

    if (nearestDotIndex !== null) {
      const prompt = prompts[nearestDotIndex];
      props.scrollToPrompt(prompt.messageId);
    } else {
      const clickRatio = Math.max(0, Math.min(1, clickY / tlHeight));
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      const targetScrollTop = clickRatio * maxScroll;

      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    }
  };

  const [dotPositionsPercent, setDotPositionsPercent] = createSignal<number[]>([]);

  // Cache positions for virtualized messages that may not be in DOM
  const cachedPositions = new Map<string, number>();

  const [timelineHeight, setTimelineHeight] = createSignal(300);

  const updateDotPositionsFromDOM = () => {
    const prompts = displayedPrompts();
    const container = props.scrollContainerRef();
    if (!container || prompts.length === 0) return;

    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    const newTimelineHeight = Math.min(clientHeight * 0.88, 600);
    setTimelineHeight(newTimelineHeight);

    const rawPositions: number[] = [];

    if (maxScroll <= 0) {
      // Content not yet laid out (e.g. virtualizer); use even distribution so dots show
      prompts.forEach((_, index) => {
        rawPositions.push(prompts.length > 1 ? (index / (prompts.length - 1)) * 100 : 0);
      });
      setDotPositionsPercent(rawPositions);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const rangeLength = maxScroll;
    const elements = indexMessageElements(container);

    prompts.forEach((prompt, index) => {
      const element = elements.get(prompt.messageId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollPos = rect.top - containerRect.top + container.scrollTop - 100;
        const percent = rangeLength > 0 ? Math.max(0, Math.min(100, (scrollPos / rangeLength) * 100)) : 0;
        rawPositions.push(percent);
        cachedPositions.set(prompt.messageId, percent);
      } else {
        const cached = cachedPositions.get(prompt.messageId);
        if (cached !== undefined) {
          rawPositions.push(cached);
        } else {
          rawPositions.push(prompts.length > 1 ? (index / (prompts.length - 1)) * 100 : 0);
        }
      }
    });

    setDotPositionsPercent(rawPositions);
  };

  onMount(() => {
    // Initial calculation after DOM is ready; staggered delays for virtualized content (e.g. shared chat)
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    const runUpdates = () => {
      updateDotPositionsFromDOM();
    };
    timeoutIds.push(setTimeout(runUpdates, 100));
    timeoutIds.push(setTimeout(runUpdates, 300));
    timeoutIds.push(setTimeout(runUpdates, 600));

    const handleResize = () => updateDotPositionsFromDOM();
    window.addEventListener("resize", handleResize);

    // Update on scroll so virtualized content (shared chat) gets accurate dot positions as user scrolls
    const container = props.scrollContainerRef();
    let scrollDebounceId: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (scrollDebounceId != null) clearTimeout(scrollDebounceId);
      scrollDebounceId = setTimeout(() => {
        updateDotPositionsFromDOM();
        scrollDebounceId = null;
      }, 150);
    };
    container?.addEventListener("scroll", handleScroll, { passive: true });

    onCleanup(() => {
      window.removeEventListener("resize", handleResize);
      container?.removeEventListener("scroll", handleScroll);
      if (scrollDebounceId != null) clearTimeout(scrollDebounceId);
      for (const id of timeoutIds) clearTimeout(id);
    });
  });

  // Re-calculate when displayed prompts change or when streaming ends (so we add the new dot once response is done)
  createEffect(() => {
    displayedPrompts().length;
    props.isStreaming();
    if (displayedPrompts().length > 0) {
      const effectTimeoutIds = [setTimeout(updateDotPositionsFromDOM, 100), setTimeout(updateDotPositionsFromDOM, 400)];
      onCleanup(() => {
        for (const id of effectTimeoutIds) clearTimeout(id);
      });
    }
  });

  // Percent (0–100) of the last displayed prompt dot; solid line goes to here, then solid to bottom
  const lastDotPercent = createMemo(() => {
    const positions = dotPositionsPercent();
    const count = displayedPrompts().length;
    if (count === 0) return 0;
    return positions[count - 1] ?? 0;
  });

  const lowerSegmentStyle = createMemo(() => {
    const top = lastDotPercent();
    const height = 100 - top;
    return {
      top: `${top}%`,
      height: `${height}%`,
      "background-color": "var(--color-dark-icon)",
      opacity: 1,
    };
  });

  const [viewportContentHeight, setViewportContentHeight] = createSignal(600);
  onMount(() => {
    const update = () => setViewportContentHeight(typeof window !== "undefined" ? window.innerHeight - 32 : 600);
    update();
    window.addEventListener("resize", update);
    onCleanup(() => window.removeEventListener("resize", update));
  });

  const [parentContainerHeight, setParentContainerHeight] = createSignal(400);
  let parentResizeObserver: ResizeObserver | undefined;
  const timelineRootRef = (el: HTMLDivElement | undefined) => {
    parentResizeObserver?.disconnect();
    parentResizeObserver = undefined;
    if (!el || props.centerInViewport) return;
    const parent = el.offsetParent;
    if (parent && parent instanceof HTMLElement) {
      const update = () => setParentContainerHeight(parent.clientHeight);
      update();
      parentResizeObserver = new ResizeObserver(update);
      parentResizeObserver.observe(parent);
    }
  };
  onCleanup(() => parentResizeObserver?.disconnect());

  // Side panel only: cap height to 80% of container so we have ~10% padding top and bottom
  const displayHeight = createMemo(() => {
    const tl = timelineHeight();
    if (props.forceVisible && !props.centerInViewport) {
      const contentH = parentContainerHeight();
      return Math.min(tl, contentH * 0.8);
    }
    return tl;
  });

  const topOffset = createMemo(() => {
    const container = props.scrollContainerRef();
    const tlHeight = timelineHeight();

    // Floating on main chat: center in viewport
    if (props.forceVisible && props.centerInViewport && typeof window !== "undefined") {
      const contentH = viewportContentHeight();
      const centered = (contentH - tlHeight) / 2;
      return Math.max(0, centered);
    }

    // Side panel "navigate this chat": 10% padding top/bottom, bar centered in middle 80%
    if (props.forceVisible && !props.centerInViewport) {
      const contentH = parentContainerHeight();
      const padding = contentH * 0.1;
      const availableHeight = contentH * 0.8;
      const height = Math.min(tlHeight, availableHeight);
      return padding + (availableHeight - height) / 2;
    }

    if (!container) return 100;
    const containerH = container.clientHeight;
    const centeredOffset = (containerH - tlHeight) / 2;
    const minOffset = containerH * 0.02;
    return Math.max(minOffset, centeredOffset);
  });

  const TIMELINE_LEFT_OFFSET = 4; // px from left edge of container

  return (
    // The dot previews are functional navigation, not help hints, so they stay
    // visible even when the user hides help tooltips (advanced mode defaults to that).
    <StTooltipSuppressionProvider suppressed={() => false}>
      <div
        ref={timelineRootRef}
        class="pointer-events-none absolute z-30 flex w-8 items-center justify-center"
        style={{
          left: `${TIMELINE_LEFT_OFFSET}px`,
          top: `${topOffset()}px`,
          height: `${displayHeight()}px`,
          overflow: "visible",
        }}
      >
        <div
          role="group"
          aria-label={props.t("prompt_timeline.label")}
          class="pointer-events-auto relative flex w-full items-center justify-center"
          style={{ height: "100%" }}
          onClick={handleTimelineClick}
          onPointerDown={() => props.setTimelinePointerActive?.(true)}
          onPointerUp={() => props.setTimelinePointerActive?.(false)}
          onPointerLeave={() => props.setTimelinePointerActive?.(false)}
        >
          <TimelineBarInner
            lastDotPercent={lastDotPercent}
            lowerSegmentStyle={lowerSegmentStyle}
            renderedHeight={displayHeight}
            promptsList={displayedPrompts}
            dotPositionsPercent={dotPositionsPercent}
            scrollToPercent={scrollToPercent}
            setIsDraggingDot={setIsDraggingDot}
            isDraggingDot={isDraggingDot}
            parentProps={props}
            handleTimelineClick={handleTimelineClick}
            setTimelinePointerActive={props.setTimelinePointerActive}
          />
        </div>
      </div>
    </StTooltipSuppressionProvider>
  );
};

const getToolIcon = (tool: ActivityCategory) => ACTIVITY_ICON_MAP[getCategoryIconKey(tool)] || TbFileText;

/** Inner content of the timeline bar (line + dots); shared when wrapping with tooltip or not. */
const TimelineBarInner = (innerProps: {
  lastDotPercent: () => number;
  lowerSegmentStyle: () => { top: string; height: string; "background-color"?: string; opacity: number };
  /** Actual rendered bar height (may be capped in side panel); use for blue-dot position so dot stays on bar. */
  renderedHeight: () => number;
  promptsList: () => Array<{
    messageId: StreamingMessageId;
    previewText: string;
    toolsUsed: ActivityCategory[];
    isPending: boolean;
    timestamp?: Date;
    username: string;
    hasBranches: boolean;
  }>;
  dotPositionsPercent: () => number[];
  scrollToPercent: (p: number) => void;
  setIsDraggingDot: (v: boolean) => void;
  isDraggingDot: () => boolean;
  parentProps: {
    scrollPercentage: () => number;
    isStreaming: () => boolean;
    t: ReturnType<typeof useI18n>["t"];
    scrollToPrompt: (messageId: StreamingMessageId) => void;
    getRelativeTime: (timestamp: Date) => string;
    timeUpdateTrigger: () => number;
  };
  handleTimelineClick: (e: MouseEvent) => void;
  /** Called when user starts/stops interacting with the blue dot (pointer down/up/cancel). */
  setTimelinePointerActive?: (active: boolean) => void;
}) => {
  const {
    lastDotPercent,
    lowerSegmentStyle,
    renderedHeight,
    promptsList,
    dotPositionsPercent,
    scrollToPercent,
    setIsDraggingDot,
    isDraggingDot,
    parentProps: props,
    handleTimelineClick,
    setTimelinePointerActive,
  } = innerProps;
  let timelineTrackRef: HTMLDivElement | null = null;
  const [lineHovered, setLineHovered] = createSignal(false);
  const [isOverDot, setIsOverDot] = createSignal(false);

  const handleLineMouseMove = () => {
    if (promptsList().length === 0) return;
    setLineHovered(true);
  };

  const handleLineMouseLeave = () => setLineHovered(false);

  return (
    <div
      ref={(el) => {
        timelineTrackRef = el ?? null;
      }}
      class="group relative w-full"
      style={{ height: "100%" }}
      onMouseLeave={handleLineMouseLeave}
    >
      {/* Solid line from top to last prompt dot */}
      <div
        class="pointer-events-none absolute top-0 left-1/2 z-0 w-px -translate-x-1/2 transition-[height,width] duration-75 ease-out group-hover:w-0.5"
        style={{
          height: `${lastDotPercent()}%`,
          "background-color": "var(--color-dark-icon)",
        }}
      />
      {/* Solid line from last dot to bottom */}
      <div
        class="pointer-events-none absolute left-1/2 z-0 w-px -translate-x-1/2 transition-[top,height,width] duration-75 ease-out group-hover:w-0.5"
        style={lowerSegmentStyle()}
      />
      {/* Invisible wider click target for the timeline; also triggers line-hover tooltips.
          Hidden from assistive tech: the scrub dot's arrow keys and the prompt dot buttons cover it. */}
      <div
        aria-hidden="true"
        class="pointer-events-auto absolute inset-y-0 left-1/2 z-0 w-6 -translate-x-1/2 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          handleTimelineClick(e);
        }}
        onMouseMove={handleLineMouseMove}
        onMouseLeave={handleLineMouseLeave}
      />
      {/* Line-hover labels: show every prompt number when hovering the track (hidden over a dot — dot has its own tooltip) */}
      <Show when={lineHovered() && !isOverDot() && promptsList().length > 0}>
        <For each={promptsList()}>
          {(_prompt, index) => (
            <div
              class="pointer-events-none absolute left-full z-40 ml-1.5 text-xs whitespace-nowrap text-disabled-text dark:text-secondary-text"
              style={{
                top: `${dotPositionsPercent()[index()] ?? 0}%`,
                transform: "translateY(-50%)",
              }}
            >
              {props.t("prompt_timeline_prompt_number", { number: String(index() + 1) })}
            </div>
          )}
        </For>
      </Show>
      {/* Blue dot indicator that follows scroll position; draggable to seek */}
      {(() => {
        const blueDotY = createMemo(() => {
          const height = renderedHeight();
          const scrollPercent = props.scrollPercentage();
          return (scrollPercent / 100) * height;
        });

        const handleDotPointerDown = (e: PointerEvent) => {
          e.stopPropagation();
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          setIsDraggingDot(true);
          setTimelinePointerActive?.(true);
        };

        const handleDotPointerMove = (e: PointerEvent) => {
          if (!isDraggingDot()) return;
          const track = timelineTrackRef;
          if (!track) return;
          const rect = track.getBoundingClientRect();
          const ratio = (e.clientY - rect.top) / rect.height;
          scrollToPercent(ratio * 100);
        };

        const handleDotPointerUpOrCancel = (e: PointerEvent) => {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
          setIsDraggingDot(false);
          setTimelinePointerActive?.(false);
        };

        // The 0.5% margin keeps a press from re-targeting the prompt the view already sits on.
        const stepToPrompt = (direction: 1 | -1) => {
          const current = props.scrollPercentage();
          const prompts = promptsList();
          const positions = dotPositionsPercent().slice(0, prompts.length);
          const index =
            direction > 0
              ? positions.findIndex((p) => p > current + 0.5)
              : positions.findLastIndex((p) => p < current - 0.5);
          if (index === -1) scrollToPercent(direction > 0 ? 100 : 0);
          else props.scrollToPrompt(prompts[index].messageId);
        };

        const handleDotKeyDown = (e: KeyboardEvent) => {
          switch (e.key) {
            case "ArrowUp":
            case "ArrowLeft":
              stepToPrompt(-1);
              break;
            case "ArrowDown":
            case "ArrowRight":
              stepToPrompt(1);
              break;
            case "PageUp":
              scrollToPercent(props.scrollPercentage() - 10);
              break;
            case "PageDown":
              scrollToPercent(props.scrollPercentage() + 10);
              break;
            case "Home":
              scrollToPercent(0);
              break;
            case "End":
              scrollToPercent(100);
              break;
            default:
              return;
          }
          e.preventDefault();
          trackFeature({ feature: "chat.timeline_jump", via: "shortcut" });
        };

        return (
          <div
            class={twMerge(
              "absolute left-1/2 z-20 select-none",
              isDraggingDot() ? "cursor-grabbing" : "cursor-grab transition-[top] duration-75 ease-out"
            )}
            style={{
              top: `${blueDotY()}px`,
              transform: "translate(-50%, -50%)",
              ...(isDraggingDot() && { transition: "none" }),
            }}
            role="slider"
            aria-label={props.t("aria_label_timeline_progress")}
            aria-orientation="vertical"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(props.scrollPercentage())}
            tabIndex={0}
            onKeyDown={handleDotKeyDown}
            onPointerDown={handleDotPointerDown}
            onPointerMove={handleDotPointerMove}
            onPointerUp={handleDotPointerUpOrCancel}
            onPointerCancel={handleDotPointerUpOrCancel}
          >
            <div class="pointer-events-none relative size-2.5 transition-all duration-200 ease-out group-hover:h-4 group-hover:w-8">
              <div class="absolute inset-0 rounded-full bg-brand opacity-90" />
              <div
                class="absolute inset-0 rounded-full blur-md transition-opacity duration-200 group-hover:opacity-0"
                style="background: radial-gradient(circle, rgba(124, 58, 237, 0.9) 0%, rgba(124, 58, 237, 0.7) 20%, rgba(139, 92, 246, 0.2) 50%, transparent 80%);"
              />
              {/* Grip lines - visible on line hover to suggest slider thumb */}
              <div class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-px opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <div class="h-px w-1.5 rounded-full bg-white/90" />
                <div class="h-px w-1.5 rounded-full bg-white/90" />
                <div class="h-px w-1.5 rounded-full bg-white/90" />
              </div>
            </div>
          </div>
        );
      })()}
      {/* Bottom dot with heartbeat glow when a response is streaming */}
      <Show when={props.isStreaming()}>
        <div
          class="pointer-events-none absolute left-1/2 z-10 flex items-center justify-center"
          style={{
            top: "100%",
            transform: "translate(-50%, -50%)",
          }}
          aria-hidden
        >
          <div class="timeline-streaming-heartbeat-dot size-1.5 rounded-full bg-dark-icon transition-all duration-75 group-hover:scale-125" />
        </div>
      </Show>
      <Show when={promptsList().length > 0}>
        <For each={promptsList()}>
          {(prompt, index) => {
            const hasTools = prompt.toolsUsed.length > 0;

            // Get the position for this dot as a percentage of timeline height (use innerProps so closure has valid reference)
            const dotPosition = () => {
              const positions = innerProps.dotPositionsPercent();
              return positions[index()] ?? 0;
            };

            // z-30 must sit on the wrapper, not just the inner trigger: the translateY
            // transform makes this a stacking context, so the trigger's own z-index can't
            // lift it above the z-20 blue scrub dot that parks on the nearest prompt dot.
            return (
              <div
                class="absolute inset-x-0 z-30 flex h-6 items-center justify-center"
                style={{
                  top: `${dotPosition()}%`,
                  transform: "translateY(-50%)",
                }}
              >
                <StTooltip
                  placement="right"
                  content={
                    <div class="relative max-w-xs">
                      <div class="absolute -top-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
                        <Show
                          when={hasTools}
                          fallback={
                            <div class="relative flex size-12 items-center justify-center rounded-full border-2 border-primary-border bg-tile">
                              <TbFileText class="relative z-10 size-8 text-gray-400" />
                            </div>
                          }
                        >
                          <For each={prompt.toolsUsed}>
                            {(tool) => {
                              const IconComponent = getToolIcon(tool);
                              return (
                                <div class="relative flex size-12 items-center justify-center rounded-full border-2 border-primary-border bg-tile">
                                  <IconComponent class="relative z-10 size-8 text-gray-400" />
                                </div>
                              );
                            }}
                          </For>
                        </Show>
                      </div>
                      <div class="px-2 pt-8 pb-2">
                        <div class="mb-1 text-sm font-bold text-primary-text">
                          {props.t("prompt_timeline_prompt_number", { number: String(index() + 1) })}:
                        </div>
                        <div class="line-clamp-3 text-sm wrap-break-word text-primary-text">
                          {prompt.previewText.replace(/\n+/g, " ").trim()}
                        </div>
                        <Show when={prompt.timestamp}>
                          {(timestamp) => (
                            <div class="mt-1 text-sm text-secondary-text">
                              {(() => {
                                props.timeUpdateTrigger();
                                const timeText = props.getRelativeTime(timestamp());
                                return `${timeText} by ${prompt.username}`;
                              })()}
                            </div>
                          )}
                        </Show>
                        <Show when={prompt.hasBranches}>
                          <div class="mt-1 flex items-center gap-1 text-xs text-violet-500 dark:text-violet-400">
                            <TbGitBranch size={12} />
                            <span>{props.t("prompt_timeline_has_branches")}</span>
                          </div>
                        </Show>
                      </div>
                    </div>
                  }
                >
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackFeature({ feature: "chat.timeline_jump", via: "button" });
                      props.scrollToPrompt(prompt.messageId);
                    }}
                    class={twMerge(
                      "pointer-events-auto absolute inset-0 z-30 cursor-pointer transition-all duration-200",
                      "flex items-center justify-center focus:outline-none"
                    )}
                    aria-label={props.t("aria_label_navigate_to_prompt", { number: String(index() + 1) })}
                    onMouseMove={handleLineMouseMove}
                    onMouseEnter={() => setIsOverDot(true)}
                    onMouseLeave={() => setIsOverDot(false)}
                  >
                    {/* Small dot - no glow, just the dot */}
                    <div class="relative z-10 hover:scale-110">
                      <Show
                        when={prompt.isPending}
                        fallback={
                          <div
                            class={twMerge(
                              "size-1.5 rounded-full bg-dark-icon transition-all duration-75 group-hover:scale-125",
                              prompt.hasBranches && "ring-2 ring-violet-400 dark:ring-violet-500"
                            )}
                          />
                        }
                      >
                        {/* Glowing dot for pending prompts */}
                        <div class="relative size-1.5 transition-transform duration-75 group-hover:scale-125">
                          <div class="absolute inset-0 rounded-full bg-brand opacity-90" />
                          <div
                            class="absolute inset-0 rounded-full blur-sm"
                            style="background: radial-gradient(circle, rgba(124, 58, 237, 0.9) 0%, rgba(124, 58, 237, 0.7) 20%, rgba(139, 92, 246, 0.2) 50%, transparent 80%);"
                          />
                        </div>
                      </Show>
                    </div>
                  </StTooltipTrigger>
                </StTooltip>
              </div>
            );
          }}
        </For>
      </Show>
    </div>
  );
};
